<template>
    <b-card class="console-section mb-3" no-body>
        <div class="section-head d-flex align-items-center justify-content-between px-3 py-2">
            <div>
                <span class="section-index">①</span>
                <span class="fw-500 ml-1">신청 질문</span>
                <span class="text-secondary small ml-2">신청자가 답하는 항목 ({{ inputKeys.length }})</span>
            </div>
            <div class="flex-shrink-0">
                <b-button size="sm" variant="outline-secondary" class="mr-1" @click="$emit('reorder')">
                    순서 변경
                </b-button>
                <b-button size="sm" variant="primary" @click="$emit('add')">
                    질문 추가
                </b-button>
            </div>
        </div>

        <div class="p-3">
            <!-- 규칙이 참조하지만 신청서에 없는 질문 (readiness.missing_keys) -->
            <div v-if="missingKeys.length" class="mb-3">
                <div
                    v-for="key in missingKeys"
                    :key="`missing-${key}`"
                    class="missing-row d-flex align-items-center justify-content-between px-3 py-2 mb-1"
                >
                    <div>
                        <i class="bi bi-exclamation-triangle-fill text-danger mr-2"></i>
                        <span class="fw-500">{{ key }}</span>
                        <span class="text-danger small ml-2">배정 규칙이 참조하지만 신청서에 없는 질문입니다.</span>
                    </div>
                    <b-button size="sm" variant="outline-danger" @click="$emit('create-missing', key)">
                        질문 만들기
                    </b-button>
                </div>
            </div>

            <p-page-empty-component v-if="inputKeys.length === 0" compact text="아직 만든 신청 질문이 없습니다."/>

            <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title. No. 는 화면 순서(sort_order) = '순서 변경'의 순서 -->
            <b-table-simple v-else responsive hover table-class="ds-table ds-table-fixed question-table">
                <b-thead>
                    <b-tr>
                        <b-th class="ds-cell-no" style="width: 56px;">No.</b-th>
                        <b-th>질문 문구</b-th>
                        <b-th style="width: 120px;">답변 형식</b-th>
                        <b-th style="width: 80px;">필수</b-th>
                        <b-th>선택지</b-th>
                        <b-th style="width: 60px;"><span class="sr-only">관리</span></b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr v-for="(q, idx) in sortedKeys" :key="q.id">
                        <b-td class="ds-cell-no">{{ idx + 1 }}</b-td>
                        <!-- 라벨이 먼저 자리를 갖고 key_name 은 남는 폭에서 줄인다 -->
                        <b-td>
                            <div class="d-flex align-items-baseline" :title="`${labelOf(q)} · ${q.key_name}`">
                                <button type="button" class="ds-detail-link ds-ellipsis flex-shrink-0" @click="$emit('edit', q)">{{ labelOf(q) }}</button>
                                <span class="ds-sub ds-ellipsis ml-1">{{ q.key_name }}</span>
                            </div>
                        </b-td>
                        <b-td>
                            <span class="ds-tag">{{ valueTypeText(q.value_type) }}</span>
                        </b-td>
                        <b-td>
                            <span :class="q.is_required ? 'ds-status ds-status-warning' : 'ds-sub'">{{ q.is_required ? '필수' : '선택' }}</span>
                        </b-td>
                        <!-- 앞 4개 + '외 N개' 를 한 줄로, 선택지 전체는 title -->
                        <b-td>
                            <template v-if="q.value_type === 'list'">
                                <span v-if="choiceLabels(q).length" class="ds-ellipsis" :title="choiceLabels(q).join(' · ')">
                                    {{ choiceLabels(q).slice(0, 4).join(' · ') }} <span v-if="choiceLabels(q).length > 4" class="ds-sub">외 {{ choiceLabels(q).length - 4 }}개</span>
                                </span>
                                <span v-else class="ds-sub">선택지 없음</span>
                            </template>
                            <span v-else class="ds-sub">-</span>
                        </b-td>
                        <b-td class="text-right">
                            <button type="button" class="ds-row-icon-btn"
                                    :aria-label="`${labelOf(q)} 편집`" title="질문 편집"
                                    @click="$emit('edit', q)">
                                <AppIcon name="pencil"/>
                            </button>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </div>
    </b-card>
</template>

<script>
import {valueTypeText} from './plain-language';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';

export default {
    name: 'QuestionSection',
    components: {
        'p-page-empty-component': PageEmptyComponent,
    },
    props: {
        // 신청 질문 목록 (schema 응답의 input_keys)
        inputKeys: {type: Array, default: () => []},
        // 규칙이 참조하지만 신청서에 없는 질문 이름들 (readiness.missing_keys)
        missingKeys: {type: Array, default: () => []},
    },
    computed: {
        sortedKeys() {
            return [...this.inputKeys].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        },
    },
    methods: {
        valueTypeText,
        labelOf(q) {
            const lt = q.label_texts || {};
            return lt.ko || q.key_name;
        },
        choiceLabel(av) {
            const lt = av.label_texts || {};
            return lt.ko || av.actual_value;
        },
        choiceLabels(q) {
            return (q.allowed_values || []).map(this.choiceLabel);
        },
    },
};
</script>

<style lang="scss" scoped>
.console-section {
    border-radius: 8px;
}

.section-head {
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
}

.section-index {
    font-weight: 700;
    color: #6c757d;
}

.missing-row {
    background: #fff5f5;
    border: 1px solid #f5c2c7;
    border-radius: 6px;
}

/* 카드 표 폭은 1366(메뉴 펼침)에서 약 1040(본문 1074 − 카드 테두리 2 − p-3 32). 고정 열 합 316(No. 56 · 형식 120 · 필수 80 · 편집 60)
   + 질문 문구 · 선택지 ~362씩. 두 칸이 200 아래로 줄지 않게 — 그보다 좁으면 가로 스크롤 */
::v-deep .question-table {
    min-width: 720px;
}
</style>
